import { useEffect, useState, useRef, useMemo } from "react";
import ServiceAreaMap from "@/components/ServiceAreaMap";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Phone,
  Mail,
  MapPin,
  Star,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Paintbrush,
  Home as HomeIcon,
  Droplets,
  Shield,
  Hammer,
  Sparkles,
  Wrench,
  Waves,
  Clock,
  Building2,
  Users,
  Award,
} from "lucide-react";
import { OpenGraphTags } from "@/components/OpenGraphTags";
import Footer from "@/components/Footer";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import GoogleReviews from "@/components/GoogleReviews";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
// StructuredData removed - using inline homepage-schema via useEffect instead
import { CanonicalTag } from "@/components/CanonicalTag";
import { getMetaKeywords } from "@/data/metaKeywords";
import { trackCTAClick, trackScrollDepth } from "@/lib/analytics";
import { trackQuoteRequest, trackPhoneCall, trackChatSubmission } from "@/lib/googleAds";

const services = [
  {
    icon: Shield,
    title: "Roof Coating & Repair",
    description: "Comprehensive roof services with GE Enduris High Solids coating. Flame-retardant protection for your property.",
  },
  {
    icon: Paintbrush,
    title: "Interior & Exterior Painting",
    description: "Precision painting of walls and ceilings with premium finishes. Transform your space beautifully.",
  },
  {
    icon: HomeIcon,
    title: "Trim & Molding",
    description: "Detail-oriented painting of baseboards, crown molding, and architectural elements.",
  },
  {
    icon: Sparkles,
    title: "Cabinet Painting",
    description: "Kitchen and bathroom cabinet transformation through expert refinishing and repainting.",
  },
  {
    icon: Hammer,
    title: "Drywall Repair",
    description: "Professional patching and repair of drywall imperfections before painting.",
  },
  {
    icon: Wrench,
    title: "Deck & Staircase Repair",
    description: "Restore and rebuild your outdoor living spaces with quality craftsmanship.",
  },
  {
    icon: Droplets,
    title: "Waterproofing",
    description: "Long-lasting protection against water damage for your home or commercial property.",
  },
  {
    icon: Waves,
    title: "Pressure Washing",
    description: "Deep cleaning services to prepare surfaces and restore their original beauty.",
  },
  {
    icon: Building2,
    title: "Commercial Painting",
    description: "Professional painting for offices, retail spaces, restaurants, and industrial facilities.",
  },
  {
    icon: Sparkles,
    title: "Venetian Plaster",
    description: "Centuries-old Italian marble-dust finish. Luminous depth and texture that no paint can replicate.",
  },
  {
    icon: Waves,
    title: "Lime Wash",
    description: "Traditional mineral finish that penetrates stucco and masonry for a breathable, organic patina.",
  },
];

const testimonials = [
  {
    text: "Danis Painting did an exceptional job on our home's exterior. Mark and his team were professional, punctual, and the quality of work exceeded our expectations. Highly recommend!",
    author: "Sarah M.",
    location: "Santa Barbara",
    tag: "Exterior Painting",
  },
  {
    text: "We hired Danis Painting for our entire interior repaint and cabinet refinishing. The attention to detail was incredible. They treated our home with respect and cleaned up every day.",
    author: "Michael R.",
    location: "Ventura",
    tag: "Interior & Cabinets",
  },
  {
    text: "Best painting company we've ever worked with! They repaired our deck and applied the roof coating. Fair pricing and outstanding workmanship. Will definitely use again.",
    author: "Jennifer L.",
    location: "Los Angeles",
    tag: "Deck & Roof",
  },
  {
    text: "Mark and the Danis Painting crew transformed our apartment complex. Very organized, great communication throughout the project. The tenants were impressed with the minimal disruption.",
    author: "David L.",
    location: "Goleta",
    tag: "Commercial Property",
  },
  {
    text: "Outstanding quality and craftsmanship! They did extensive drywall repair and painting in our home. The results were flawless. True professionals in every sense.",
    author: "Susan K.",
    location: "Montecito",
    tag: "Drywall & Painting",
  },
  {
    text: "After the fires, we needed flame-retardant roof coating. Danis Painting came through with excellent service and gave us peace of mind. Grateful for their expertise.",
    author: "Michael T.",
    location: "Santa Barbara",
    tag: "Flame-Retardant Coating",
  },
];

const features = [
  "Licensed and fully insured professionals",
  "Premium quality materials and finishes",
  "Detailed preparation and clean worksite",
  "Free estimates and competitive pricing",
  "Residential and commercial expertise",
  "Flame-retardant roof coating specialists",
];

const galleryImages = [
  { src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/gallery-5_d36da2e9_a4c69cb6.webp", title: "Santa Barbara Apartment Complex", description: "Full exterior paint job for a 9 unit apartment complex", width: 1200, height: 900 },
  { src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/gallery-1_dc744938_c2f9cc92.webp", title: "Wrought Iron Installation", description: "Custom wrought iron work and painting", width: 1200, height: 900 },
  { src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/gallery-7_a305a628_448adb01.webp", title: "Deck Repair", description: "Complete deck restoration and refinishing", width: 1200, height: 900 },
  { src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/gallery-2_f686ddc5_fd9353af.webp", title: "Siding Repair", description: "Wood siding repair and painting", width: 1200, height: 900 },
  { src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/gallery-4_027edc9e_b7efd3ba.webp", title: "Roof Coating & Repair", description: "GE Enduris flame-retardant coating application", width: 1200, height: 900 },
  { src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/gallery-8_298dbede_0e575d13.webp", title: "Ventura Home", description: "Complete interior and exterior transformation", width: 1200, height: 900 },
  { src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/gallery-3_c5b2e676_b82fb74a.webp", title: "Residential Project", description: "Premium residential painting services", width: 1200, height: 900 },
  { src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/gallery-6_9e226298_8f60dfb4.webp", title: "Commercial Property", description: "Professional commercial painting", width: 1200, height: 900 },
];

// Quote Form Component
function QuoteForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    serviceType: "",
    propertyType: "residential" as "residential" | "commercial",
    projectDescription: "",
    budgetRange: "",
    timeline: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submitQuote = trpc.leads.submitQuote.useMutation({
    onSuccess: (data) => {
      setSubmitted(true);
      toast.success("Quote request submitted! We'll contact you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        serviceType: "",
        propertyType: "residential",
        projectDescription: "",
        budgetRange: "",
        timeline: "",
      });
      setTimeout(() => setSubmitted(false), 3000);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to submit quote request");
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitQuote.mutateAsync(formData);
      trackQuoteRequest(); // Track Google Ads conversion
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <div className="text-green-600 text-lg font-semibold mb-2">✓ Quote Request Submitted!</div>
        <p className="text-green-700">Thank you for contacting Danis Painting Co. We'll review your request and contact you within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Input
          placeholder="Your Name *"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
        />
        <Input
          placeholder="Email Address *"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Input
          placeholder="Phone Number *"
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          required
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
        />
        <Input
          placeholder="Address"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Select value={formData.serviceType} onValueChange={(value) => setFormData({ ...formData, serviceType: value })}>
            <SelectTrigger className="bg-white/10 border-white/20 text-white">
              <SelectValue placeholder="Select Service *" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Interior Painting">Interior Painting</SelectItem>
              <SelectItem value="Exterior Painting">Exterior Painting</SelectItem>
              <SelectItem value="Cabinet Painting">Cabinet Painting</SelectItem>
              <SelectItem value="Roof Coating">Roof Coating</SelectItem>
              <SelectItem value="Waterproofing">Waterproofing</SelectItem>
              <SelectItem value="Drywall Repair">Drywall Repair</SelectItem>
              <SelectItem value="Deck Repair">Deck Repair</SelectItem>
              <SelectItem value="Commercial Painting">Commercial Painting</SelectItem>
              <SelectItem value="Venetian Plaster">Venetian Plaster</SelectItem>
              <SelectItem value="Lime Wash">Lime Wash</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Select value={formData.propertyType} onValueChange={(value) => setFormData({ ...formData, propertyType: value as "residential" | "commercial" })}>
            <SelectTrigger className="bg-white/10 border-white/20 text-white">
              <SelectValue placeholder="Property Type *" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="residential">Residential</SelectItem>
              <SelectItem value="commercial">Commercial</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Textarea
        placeholder="Describe your project in detail *"
        value={formData.projectDescription}
        onChange={(e) => setFormData({ ...formData, projectDescription: e.target.value })}
        required
        minLength={10}
        className="bg-white/10 border-white/20 text-white placeholder:text-white/50 min-h-24"
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <Input
          placeholder="Budget Range (e.g., $5,000 - $10,000)"
          value={formData.budgetRange}
          onChange={(e) => setFormData({ ...formData, budgetRange: e.target.value })}
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
        />
        <Input
          placeholder="Timeline (e.g., ASAP, 2-4 weeks)"
          value={formData.timeline}
          onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting || submitQuote.isPending}
        className="w-full bg-white text-[#1e3a8a] hover:bg-white/90 font-semibold"
        size="lg"
      >
        {isSubmitting || submitQuote.isPending ? "Submitting..." : "Get Free Quote"}
      </Button>

      <p className="text-white/60 text-xs text-center">* Required fields</p>
    </form>
  );
}

export default function Home() {
  // Structured data injected via useEffect as homepage-schema
  const canonicalUrl = "https://danispainting.net/";

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServiceAreasOpen, setMobileServiceAreasOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Full pool of 29 blog articles — 3 randomly selected on each page load
  const featuredBlogPosts = useMemo(() => {
    const CDN = 'https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww';
    const allPosts = [
      { slug: 'exterior-painting-guide', title: 'Complete Guide to Exterior Painting', excerpt: 'Learn the essential steps for professional exterior painting results that last for years.', readingTime: 12, category: 'Exterior', image: `${CDN}/blog-exterior-painting_f9934eb9_f0986be5.webp` },
      { slug: 'waterproofing-importance', title: 'Why Waterproofing is Essential', excerpt: 'Protect your property from water damage with professional waterproofing solutions.', readingTime: 10, category: 'Waterproofing', image: `${CDN}/blog-waterproofing_fe3affeb_8d432c7d.webp` },
      { slug: 'interior-painting-trends', title: '2025 Interior Painting Trends', excerpt: 'Discover the latest color palettes and painting techniques for modern interiors.', readingTime: 8, category: 'Interior', image: `${CDN}/blog-interior-painting-trends_6b2b639b_8356ff26.webp` },
      { slug: 'commercial-painting-guide', title: 'Commercial Painting Best Practices', excerpt: 'Professional painting solutions for businesses that minimize downtime.', readingTime: 14, category: 'Commercial', image: `${CDN}/blog-commercial-painting_4782affb_0956e0a9.webp` },
      { slug: 'deck-maintenance-guide', title: 'Deck Maintenance & Painting Guide', excerpt: 'Keep your outdoor spaces beautiful and protected with proper maintenance.', readingTime: 11, category: 'Outdoor', image: `${CDN}/blog-deck-maintenance_cee34e8b_f0526cc0.webp` },
      { slug: 'roof-coating-benefits', title: 'Benefits of Professional Roof Coating', excerpt: "Extend your roof's lifespan and improve energy efficiency with quality coatings.", readingTime: 9, category: 'Roofing', image: `${CDN}/blog-roof-coating_e8a219ba_061b1643.webp` },
      { slug: 'cabinet-painting-transform-kitchen', title: 'Cabinet Painting: Transform Your Kitchen', excerpt: 'A cost-effective way to refresh your kitchen without a full renovation.', readingTime: 10, category: 'Interior', image: `${CDN}/blog-cabinet-painting_cdf76925.jpg` },
      { slug: 'drywall-repair-preparation', title: 'Drywall Repair Before Painting', excerpt: 'Why proper drywall repair is essential for a flawless paint finish.', readingTime: 8, category: 'Repair', image: `${CDN}/blog-drywall-repair_06821c84_6df9c301.webp` },
      { slug: 'pressure-washing-benefits', title: 'Pressure Washing Before Painting', excerpt: 'How pressure washing prepares surfaces for a longer-lasting paint job.', readingTime: 7, category: 'Exterior', image: `${CDN}/blog-pressure-washing_b3ed6d32_07188db2.webp` },
      { slug: 'trim-molding-finishing-touches', title: 'Painting Trim & Molding Like a Pro', excerpt: 'Tips for achieving crisp, clean lines on trim and molding.', readingTime: 9, category: 'Interior', image: `${CDN}/blog-trim-molding_f89cfade_70c542f3.webp` },
      { slug: 'santa-barbara-painters', title: 'Best Painters in Santa Barbara', excerpt: 'What to look for when hiring a painting contractor in Santa Barbara.', readingTime: 11, category: 'Santa Barbara', image: `${CDN}/hero-santa-barbara_8efb2d2c_41509eb6.webp` },
      { slug: 'ventura-painting-services', title: 'Painting Services in Ventura', excerpt: "Professional painting solutions for Ventura's coastal homes and businesses.", readingTime: 10, category: 'Ventura', image: `${CDN}/blog-ventura-painting_486e0105_cafd3961.webp` },
      { slug: 'goleta-house-painters', title: 'Painters in Goleta', excerpt: 'Expert painting services for Goleta homes near UCSB and Camino Real.', readingTime: 10, category: 'Goleta', image: `${CDN}/blog-goleta-painters-v2-PhEGoeEmvBEqnhCjmRS9yp_cdaf178c.webp` },
      { slug: 'montecito-luxury-painting', title: 'Luxury Painting in Montecito', excerpt: "High-end painting services for Montecito's prestigious estates and villas.", readingTime: 11, category: 'Montecito', image: `${CDN}/blog-montecito-luxury_1ee4f51e_1b327e54.webp` },
      { slug: 'carpinteria-painting-contractors', title: 'Painting Contractors in Carpinteria', excerpt: 'Coastal painting expertise for Carpinteria homes and Linden Avenue businesses.', readingTime: 10, category: 'Carpinteria', image: `${CDN}/blog-carpinteria-coastal_8e0279fa.jpg` },
      { slug: 'ojai-painting-services', title: 'Painting Services in Ojai', excerpt: "Preserving Ojai's unique architectural character with expert painting.", readingTime: 10, category: 'Ojai', image: `${CDN}/blog-ojai-painting_445ffda3_376f8d7b.webp` },
      { slug: 'camarillo-painters', title: 'Painters in Camarillo', excerpt: "Professional painting for Camarillo's master-planned communities and HOAs.", readingTime: 10, category: 'Camarillo', image: `${CDN}/blog-camarillo-painters_1e90c20e.jpg` },
      { slug: 'solvang-painting-restoration', title: 'Painting Services in Solvang', excerpt: "Authentic Danish-style painting for Solvang's historic village architecture.", readingTime: 10, category: 'Solvang', image: `${CDN}/blog-solvang-painting_8cbe508d_337b97a5.webp` },
      { slug: 'buellton-house-painting', title: 'Painters in Buellton', excerpt: "Painting solutions for Buellton's growing residential and commercial properties.", readingTime: 10, category: 'Buellton', image: `${CDN}/blog-buellton-painting_7f798c98.jpg` },
      { slug: 'santa-ynez-painting-experts', title: 'Painting in Santa Ynez Valley', excerpt: 'Wine country painting expertise for Santa Ynez Valley ranches and estates.', readingTime: 10, category: 'Santa Ynez', image: `${CDN}/blog-santa-ynez-painting_dad445ea_8e21f6d4.webp` },
      { slug: 'isla-vista-student-housing-painting', title: 'Painters in Isla Vista', excerpt: "Affordable, quality painting for Isla Vista's student and rental properties.", readingTime: 10, category: 'Isla Vista', image: `${CDN}/blog-isla-vista-painting_8eeecf0b_d3bccf34.webp` },
      { slug: 'goleta-commercial-painting-guide', title: 'Commercial Painting in Goleta', excerpt: "Expert commercial painting for Goleta's tech campuses and retail centers.", readingTime: 10, category: 'Commercial', image: `${CDN}/blog-goleta-commercial-v2-KxfP3BxcY3sDaXD6iEkaxN_4f49e6bf.webp` },
      { slug: 'ventura-commercial-painting', title: 'Commercial Painting in Ventura', excerpt: 'Professional commercial painting for Ventura Harbor and Main Street businesses.', readingTime: 10, category: 'Commercial', image: `${CDN}/blog-ventura-commercial_c31c3b5c_fe7990cc.webp` },
      { slug: 'carpinteria-commercial-painting', title: 'Commercial Painting in Carpinteria', excerpt: "Marine-grade coatings and commercial painting for Carpinteria's coastal businesses.", readingTime: 10, category: 'Commercial', image: `${CDN}/blog-carpinteria-commercial_50b3f895_e6608e9d.webp` },
      { slug: 'montecito-luxury-painting', title: 'Luxury Home Painting in Montecito', excerpt: "Premium painting services for Montecito's most prestigious estates.", readingTime: 11, category: 'Montecito', image: `${CDN}/blog-montecito-luxury_1ee4f51e_1b327e54.webp` },
      { slug: 'ojai-exterior-painting', title: 'Exterior Painting in Ojai', excerpt: 'Heat-resistant coatings and exterior painting for Ojai Valley homes.', readingTime: 10, category: 'Exterior', image: `${CDN}/blog-ojai-painting_445ffda3_376f8d7b.webp` },
      { slug: 'camarillo-hoa-painting', title: 'HOA Painting in Camarillo', excerpt: "Compliant, efficient painting for Camarillo's HOA-managed communities.", readingTime: 10, category: 'HOA', image: `${CDN}/blog-camarillo-painters_1e90c20e.jpg` },
      { slug: 'summerland-coastal-paint-protection', title: 'Painters in Summerland', excerpt: "Coastal painting expertise for Summerland's charming beachside cottages.", readingTime: 9, category: 'Summerland', image: `${CDN}/blog-summerland-coastal_3e30efbc_ace43cfd.webp` },
      { slug: 'oxnard-painting-services', title: 'Painting Services in Oxnard', excerpt: "Professional painting for Oxnard's diverse residential and commercial properties.", readingTime: 10, category: 'Oxnard', image: `${CDN}/blog-oxnard-waterfront_ebdf7879_3ac9b52c.webp` },
    ];
    return [...allPosts].sort(() => Math.random() - 0.5).slice(0, 3);
  }, []);

  useEffect(() => {
    document.title = "Award Winning Santa Barbara, Goleta and Ventura Painters | Danis Painting Co.";
    
    // Handle hash navigation from other pages
    const hash = window.location.hash.substring(1); // Remove the # symbol
    if (hash) {
      // Wait for page to fully load before scrolling
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
    
    // Add meta keywords
    let metaKeywordsTag = document.querySelector('meta[name="keywords"]');
    if (!metaKeywordsTag) {
      metaKeywordsTag = document.createElement('meta');
      metaKeywordsTag.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywordsTag);
    }
    metaKeywordsTag.setAttribute('content', getMetaKeywords('home'));

    // Add comprehensive LocalBusiness schema
    const schema = {
      "@context": "https://schema.org",
      "@type": "HousePainter",
      "@id": "https://danispainting.net/#organization",
      "name": "Danis Painting Co.",
      "alternateName": "Mark Danis DBA Danis Painting",
      "url": "https://danispainting.net",
      "logo": "https://danispainting.net/logo.png",
      "image": "https://danispainting.net/logo.png",
      "description": "Professional painting and waterproofing services in Santa Barbara, Ventura, and Los Angeles. Over 30 years of exceptional quality and craftsmanship.",
      "telephone": "+1-805-403-8727",
      "email": "danispainting@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "133 E De La Guerra St, #267",
        "addressLocality": "Santa Barbara",
        "addressRegion": "CA",
        "postalCode": "93101",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "34.4208",
        "longitude": "-119.6982"
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "00:00",
          "closes": "23:59"
        }
      ],
      "priceRange": "$$",
      "paymentAccepted": ["Cash", "Check", "Credit Card", "Debit Card", "Visa", "Mastercard", "American Express"],
      "areaServed": [
        {
          "@type": "City",
          "name": "Santa Barbara",
          "@id": "https://en.wikipedia.org/wiki/Santa_Barbara,_California",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Santa Barbara County" },
          "geo": { "@type": "GeoCoordinates", "latitude": 34.4208, "longitude": -119.6982 }
        },
        {
          "@type": "City",
          "name": "Ventura",
          "@id": "https://en.wikipedia.org/wiki/Ventura,_California",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Ventura County" },
          "geo": { "@type": "GeoCoordinates", "latitude": 34.2746, "longitude": -119.2290 }
        },
        {
          "@type": "City",
          "name": "Goleta",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Santa Barbara County" },
          "geo": { "@type": "GeoCoordinates", "latitude": 34.4358, "longitude": -119.8276 }
        },
        {
          "@type": "City",
          "name": "Montecito",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Santa Barbara County" },
          "geo": { "@type": "GeoCoordinates", "latitude": 34.4367, "longitude": -119.6318 }
        },
        {
          "@type": "City",
          "name": "Carpinteria",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Santa Barbara County" },
          "geo": { "@type": "GeoCoordinates", "latitude": 34.3989, "longitude": -119.5184 }
        },
        {
          "@type": "City",
          "name": "Ojai",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Ventura County" },
          "geo": { "@type": "GeoCoordinates", "latitude": 34.4480, "longitude": -119.2429 }
        },
        {
          "@type": "City",
          "name": "Camarillo",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Ventura County" },
          "geo": { "@type": "GeoCoordinates", "latitude": 34.2164, "longitude": -119.0376 }
        },
        {
          "@type": "City",
          "name": "Solvang",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Santa Barbara County" },
          "geo": { "@type": "GeoCoordinates", "latitude": 34.5958, "longitude": -120.1376 }
        },
        {
          "@type": "City",
          "name": "Buellton",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Santa Barbara County" },
          "geo": { "@type": "GeoCoordinates", "latitude": 34.6133, "longitude": -120.1926 }
        },
        {
          "@type": "City",
          "name": "Santa Ynez",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Santa Barbara County" },
          "geo": { "@type": "GeoCoordinates", "latitude": 34.6128, "longitude": -120.0798 }
        },
        {
          "@type": "City",
          "name": "Isla Vista",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Santa Barbara County" },
          "geo": { "@type": "GeoCoordinates", "latitude": 34.4133, "longitude": -119.8610 }
        },
        {
          "@type": "City",
          "name": "Los Angeles",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Los Angeles County" },
          "geo": { "@type": "GeoCoordinates", "latitude": 34.0522, "longitude": -118.2437 }
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Painting and Waterproofing Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Roof Coating & Repair",
              "description": "Comprehensive roof services with GE Enduris High Solids coating. Flame-retardant protection."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Interior & Exterior Painting",
              "description": "Precision painting of walls and ceilings with premium finishes."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Cabinet Painting",
              "description": "Kitchen and bathroom cabinet transformation with professional finishes."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Waterproofing",
              "description": "Comprehensive waterproofing solutions for decks, balconies, and exterior surfaces."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Pressure Washing",
              "description": "Professional cleaning services for surfaces before painting or maintenance."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Drywall Repair",
              "description": "Expert drywall patching and repair services."
            }
          }
        ]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "24",
        "bestRating": "5",
        "worstRating": "1"
      },
      "founder": {
        "@type": "Person",
        "name": "Mark Danis"
      },
      "foundingDate": "1995",
      "slogan": "Honest New England Quality",
      "knowsAbout": ["Painting", "Waterproofing", "Roof Coating", "Cabinet Refinishing", "Pressure Washing", "Drywall Repair"],
      "hasMap": "https://www.google.com/maps/place/Danis+Painting+Co./@34.4217374,-119.6976324,17z/data=!3m1!4b1!4m6!3m5!1s0x80e9151e40117129:0xc31253e4eab1f0c9",
      "sameAs": [
        "https://www.google.com/maps/place/Danis+Painting+Co./@34.4217374,-119.6976324,17z/data=!3m1!4b1!4m6!3m5!1s0x80e9151e40117129:0xc31253e4eab1f0c9",
        "https://www.facebook.com/p/Mark-Danis-Painting-Co-100051124372469/",
        "https://www.instagram.com/danispaintingco/",
        "https://www.yelp.com/biz/danis-painting-santa-barbara-4",
        "https://www.bbb.org/us/ca/santa-barbara/profile/painting-contractors/danis-painting-1236-92008136",
        "https://www.angi.com/companylist/us/ca/santa-barbara/danis-painting-reviews-6175837.htm",
        "https://www.homeadvisor.com/rated.DANISPAINTINGCO.104191503.html"
      ],
      "hasCredential": {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "license",
        "name": "California Contractor License",
        "identifier": "662554",
        "recognizedBy": {
          "@type": "Organization",
          "name": "California Contractors State License Board"
        }
      },
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "License Number",
          "value": "LIC #662554"
        },
        {
          "@type": "PropertyValue",
          "name": "Issuing Authority",
          "value": "California Contractors State License Board (CSLB)"
        }
      ]
    };

    let script = document.querySelector('script[type="application/ld+json"]#homepage-schema') as HTMLScriptElement;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'homepage-schema';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]#homepage-schema');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const unsubscribe = trackScrollDepth();
    return () => unsubscribe();
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setMobileServiceAreasOpen(false);
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const scrollToSection = (id: string) => {
    if (id === 'blog') {
      window.location.href = '/blog';
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  // Lightbox functions
  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  // Swipe gesture handling for lightbox
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    setTouchEnd(e.changedTouches[0].clientX);
  };

  useEffect(() => {
    if (touchStart - touchEnd > 50) {
      // Swiped left, show next image
      nextImage();
    }
    if (touchStart - touchEnd < -50) {
      // Swiped right, show previous image
      prevImage();
    }
  }, [touchStart, touchEnd]);
  return (
    <div className="min-h-screen bg-background">
      {/* Canonical URL for SEO */}
      <CanonicalTag url={canonicalUrl} />
      
      {/* Breadcrumb Schema for SEO */}
      <BreadcrumbSchema items={[{ label: "Home", url: "/" }]} />
      
      {/* Open Graph & Twitter Card Meta Tags */}
      <OpenGraphTags
        title="Award Winning Santa Barbara, Goleta and Ventura Painters | Danis Painting Co."
        description="Professional painting and waterproofing services in Santa Barbara, Ventura, and surrounding areas. Over 30 years of exceptional quality, 5-star rated. Interior, exterior, commercial painting, roof coating, and more."
        image="https://danispainting.net/logo.png"
        url="https://danispainting.net/"
        type="website"
        siteName="Danis Painting Co."
      />
      
      {/* Hreflang Tags for Spanish alternate */}
      
      {/* Structured Data injected via useEffect as homepage-schema */}
      
      {/* Navigation */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-[#0a1628]/95 backdrop-blur-md shadow-lg" : "bg-transparent"
        }`}
      >
        <nav className="container flex items-center justify-between py-3 md:py-4">
          <a href="#" className="flex items-center flex-shrink-0">
            <img 
              src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663313033271/KuIXmYeHtVqHONwe.png" 
              alt="Danis Painting Co. — Award-Winning Painting Contractor in Santa Barbara & Ventura" 
              className="h-10 sm:h-12 md:h-14 w-auto max-w-[180px] sm:max-w-[220px] md:max-w-none object-contain"
              fetchPriority="high"
              width="220" height="56"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {["Home", "About", "Services", "Gallery", "Testimonials", "Blog", "Contact"].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item.toLowerCase())}
                className="text-white/90 hover:text-white transition-colors text-sm font-medium whitespace-nowrap"
              >
                {item}
              </button>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <Button
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 bg-transparent text-sm"
              onClick={() => scrollToSection("contact")}
            >
              Get Quote
            </Button>
            <Button
              className="bg-[#4caf30] hover:bg-[#3d9426] text-white font-semibold text-sm"
              onClick={() => {
                trackPhoneCall();
                window.location.href = "tel:805-403-8727";
              }}
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2 relative z-[60]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            <div className="relative w-6 h-6">
              <Menu className={`w-6 h-6 absolute inset-0 transition-all duration-300 ${mobileMenuOpen ? 'opacity-0 rotate-90 scale-75' : 'opacity-100 rotate-0 scale-100'}`} />
              <X className={`w-6 h-6 absolute inset-0 transition-all duration-300 ${mobileMenuOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-75'}`} />
            </div>
          </button>
        </nav>
      </header>

      {/* Mobile Menu Overlay - OUTSIDE header to avoid stacking context issues */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[55] transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu Slide-in Panel - OUTSIDE header to avoid stacking context issues */}
      <div
        className={`lg:hidden fixed top-0 right-0 h-full w-[85%] max-w-[360px] bg-[#0a1628] z-[60] shadow-2xl transform transition-transform duration-300 ease-out ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
          <div className="flex flex-col h-full overflow-y-auto">
            {/* Menu Header */}
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <img
                src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663313033271/KuIXmYeHtVqHONwe.png"
                alt="Danis Painting Co. — Painting Contractors Santa Barbara & Ventura"
                className="h-10 w-auto object-contain"
                loading="lazy"
                width="160" height="40"
              />
              <button
                className="text-white/70 hover:text-white p-1 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Navigation Links */}
            <div className="flex-1 py-4">
              <div className="px-5 space-y-1">
                {["Home", "About", "Services", "Gallery", "Testimonials", "Blog", "Contact"].map((item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item.toLowerCase())}
                    className="block w-full text-white/90 hover:text-white hover:bg-white/5 transition-all text-left py-3 px-3 rounded-lg text-base font-medium"
                  >
                    {item}
                  </button>
                ))}
              </div>

              {/* Service Areas Section */}
              <div className="mt-4 border-t border-white/10 pt-4 px-5">
                <button
                  onClick={() => setMobileServiceAreasOpen(!mobileServiceAreasOpen)}
                  className="flex items-center justify-between w-full text-white/70 hover:text-white transition-colors py-2 px-3 text-sm font-semibold uppercase tracking-wider"
                >
                  <span className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Service Areas
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileServiceAreasOpen ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ease-out ${mobileServiceAreasOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="grid grid-cols-2 gap-1 pt-2 pb-2">
                    {[
                      { name: "Santa Barbara", slug: "santa-barbara" },
                      { name: "Ventura", slug: "ventura" },
                      { name: "Montecito", slug: "montecito" },
                      { name: "Goleta", slug: "goleta" },
                      { name: "Carpinteria", slug: "carpinteria" },
                      { name: "Ojai", slug: "ojai" },
                      { name: "Camarillo", slug: "camarillo" },
                      { name: "Solvang", slug: "solvang" },
                      { name: "Buellton", slug: "buellton" },
                      { name: "Santa Ynez", slug: "santa-ynez" },
                      { name: "Isla Vista", slug: "isla-vista" },
                      { name: "Summerland", slug: "summerland" },
                      { name: "Oxnard", slug: "oxnard" },
                      { name: "Los Angeles", slug: "los-angeles" },
                    ].map((area) => (
                      <Link
                        key={area.slug}
                        href={`/city/${area.slug}`}
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-white/70 hover:text-white hover:bg-white/5 transition-all py-2 px-3 rounded-lg text-sm"
                      >
                        {area.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom CTA Section */}
            <div className="p-5 border-t border-white/10 space-y-3">
              <Button
                className="bg-[#4caf30] hover:bg-[#3d9426] text-white font-semibold w-full h-12 text-base"
                onClick={() => {
                  trackPhoneCall();
                  window.location.href = "tel:805-403-8727";
                }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now: 805-403-8727
              </Button>
              <Button
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 bg-transparent w-full h-12 text-base"
                onClick={() => {
                  scrollToSection("contact");
                  setMobileMenuOpen(false);
                }}
              >
                Get Free Quote
              </Button>
            </div>
          </div>
      </div>

      {/* Hero Section */}
      <section
        id="home"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #0a1628 100%)",
        }}
      >
        {/* Background Image Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: "url('https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/hero-santa-barbara_8efb2d2c_41509eb6.webp')",
          }}
        />
        
        <div className="container relative z-10 text-center py-20 mt-16 px-4">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
            <span className="text-white text-sm">Best of 2025 Santa Barbara Award Winner</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[62px] font-bold text-white mb-4 leading-tight">
            Award-Winning Painting Contractor in
            <br />
            <span className="text-[#22d3ee]">Santa Barbara, Goleta & Ventura</span>
          </h1>

          <p className="text-white/80 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 px-4">
            Over 30 years of exceptional quality and craftsmanship. Serving Santa Barbara, Ventura, and Los Angeles with honest New England quality.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
            <Button
              size="lg"
              className="bg-[#4caf30] hover:bg-[#3d9426] text-white font-semibold px-6 sm:px-8 text-sm sm:text-base"
              onClick={() => {
                trackCTAClick('call_now', 'content');
                window.location.href = "tel:805-403-8727";
              }}
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Call Now: 805-403-8727
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 bg-white/5 px-6 sm:px-8 text-sm sm:text-base"
              onClick={() => scrollToSection("contact")}
            >
              Get Free Quote
            </Button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <button
          onClick={() => scrollToSection("about")}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors animate-bounce"
        >
          <ChevronDown className="w-8 h-8" />
        </button>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 md:py-20 bg-white">
        <div className="container px-4">
          <div className="max-w-6xl mx-auto">
            <p className="text-[#1e3a8a] font-semibold mb-2 text-sm tracking-wider text-center">ABOUT US</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0a1628] mb-10 text-center">
              The Company That Cares
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Left Column: Painter image */}
              <div>
                <div className="relative">
                  <div className="overflow-hidden rounded-lg shadow-xl">
                    <img
                      loading="eager"
                      fetchPriority="low"
                      src="https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/award-section-painter_2d49385e.webp"
                      alt="Award-winning professional painter from Danis Painting Co. working on Santa Barbara home exterior"
                      width="800"
                      height="534"
                      className="w-full object-cover object-center h-[300px] sm:h-[350px] lg:h-[380px]"
                    />
                  </div>
                  <div className="absolute bottom-4 left-4 bg-[#0a1628] text-white p-3 rounded-lg shadow-lg flex items-center gap-2">
                    <img loading="lazy" src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663313033271/KuIXmYeHtVqHONwe.png" alt="Danis Painting Co. Logo" className="h-8 w-auto" />
                    <div className="text-xs">
                      <p className="font-bold">Best of Santa Barbara</p>
                      <p className="text-white/70">2025 Award Winner</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Award + Stats */}
              <div className="flex flex-col items-center justify-center">
                <h3 className="text-xl md:text-2xl font-bold text-[#0a1628] mb-6 text-center">Award-Winning Excellence</h3>
                <div className="flex justify-center mb-6">
                  <img
                    src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663313033271/RZupbVUNxnaoQdES.png"
                    alt="Best of Santa Barbara 2025 - Danis Painting Co. ranked #1 in Santa Barbara, 93101"
                    className="w-full max-w-xs h-auto rounded-lg shadow-md hover:shadow-lg transition-shadow"
                    loading="lazy"
                    width="320" height="320"
                  />
                </div>
                <p className="text-gray-600 text-sm md:text-base text-center">
                  Recognized as the #1 painting contractor in Santa Barbara for 2025, Danis Painting Co. continues to deliver award-winning quality and service excellence. We are a proud <strong>A+ BBB Rated</strong> contractor — accredited since 2026.
                </p>
              </div>
            </div>

            {/* Stats below the two columns */}
            <div className="grid grid-cols-3 gap-4 mt-10 pt-6 border-t border-gray-200">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-[#4caf30] mb-1">
                  <Clock className="w-5 h-5" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-[#0a1628]">30+</p>
                <p className="text-xs text-gray-500">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-[#4caf30] mb-1">
                  <Users className="w-5 h-5" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-[#0a1628]">500+</p>
                <p className="text-xs text-gray-500">Happy Clients</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-[#4caf30] mb-1">
                  <Award className="w-5 h-5" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-[#0a1628]">#1</p>
                <p className="text-xs text-gray-500">Best of Santa Barbara 2025</p>
              </div>
            </div>

            {/* Text and features below the stats */}
            <div className="mt-8">
              <p className="text-gray-600 mb-4 leading-relaxed">
                From the European Wax Center to some of the nicest houses in Santa Barbara and Ventura, Danis Painting Company is a trusted painting contractor that understands the importance of high-quality work over rushing and cutting corners.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                We are dedicated to providing the best possible service to our community, from small houses to HOAs and apartment complexes. Owner Mark Danis brings honest New England quality to every jobsite.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#4caf30]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#4caf30]" />
                    </div>
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 md:py-20 bg-gray-50">
        <div className="container px-4">
          <div className="text-center mb-12">
            <p className="text-[#1e3a8a] font-semibold mb-2 text-sm tracking-wider">WHAT WE DO</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0a1628] mb-4">Professional Painting & Maintenance Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From residential homes to commercial properties, we deliver exceptional quality on every project.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {services.map((service, index) => {
              const serviceSlug = service.title
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[&]/g, '')
                .replace(/--/g, '-')

              return (
                <a href={`/service/${serviceSlug}`} key={index}>
                  <Card
                    className="group hover:shadow-lg transition-all duration-300 border-gray-200 hover:border-[#4caf30]/30 bg-white cursor-pointer h-full"
                  >
                    <CardContent className="p-5 md:p-6">
                      <div className="w-12 h-12 rounded-lg bg-[#4caf30]/10 flex items-center justify-center mb-4 group-hover:bg-[#4caf30]/20 transition-colors">
                        <service.icon className="w-6 h-6 text-[#4caf30]" />
                      </div>
                      <h3 className="font-semibold text-[#0a1628] mb-2">{service.title}</h3>
                      <p className="text-gray-600 text-sm">{service.description}</p>
                    </CardContent>
                  </Card>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commercial Painting Focus Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-[#1e3a8a] to-[#0066cc] text-white">
        <div className="container px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-[#22d3ee] font-semibold mb-3 text-sm tracking-wider">COMMERCIAL SERVICES</p>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Professional Commercial Painting Contractors</h2>
                <p className="text-white/90 text-lg mb-6">
                  Danis Painting Co. is the trusted commercial painting contractor for businesses throughout Santa Barbara, Ventura, and Los Angeles counties. As licensed painting contractors with over 30 years of experience, we've transformed hundreds of offices, retail stores, restaurants, warehouses, and industrial facilities.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-[#4caf30] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold mb-1">Zero Business Disruption</h3>
                      <p className="text-white/80 text-sm">Flexible evening, weekend, and holiday scheduling to keep your business running</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-[#4caf30] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold mb-1">Commercial-Grade Expertise</h3>
                      <p className="text-white/80 text-sm">Specialized coatings, epoxy floors, anti-graffiti finishes, fire-resistant paints</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-[#4caf30] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold mb-1">Licensed & Warranty-Backed</h3>
                      <p className="text-white/80 text-sm">Fully licensed and insured, OSHA-compliant, 5-year warranty on all projects</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link href="/service/commercial-painting">
                    <Button size="lg" className="bg-white text-[#1e3a8a] hover:bg-white/90 w-full sm:w-auto">
                      View Commercial Services
                    </Button>
                  </Link>
                  <Link href="/service/commercial-painting/santa-barbara">
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto">
                      Santa Barbara Downtown
                      <ChevronRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link href="/service/commercial-painting/goleta">
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto">
                      Goleta UCSB Area
                      <ChevronRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-white text-white hover:bg-white/10 w-full sm:w-auto"
                    onClick={() => window.dispatchEvent(new CustomEvent('openChat'))}
                  >
                    Get Free Estimate
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 border border-white/20">
                  <h3 className="text-xl font-bold mb-6">Commercial Projects We Handle</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      "Office Buildings",
                      "Retail Stores",
                      "Restaurants & Cafes",
                      "Medical Facilities",
                      "Warehouses",
                      "Industrial Plants",
                      "Shopping Centers",
                      "Hotels & Resorts",
                      "Schools & Universities",
                      "Apartment Complexes",
                      "Parking Structures",
                      "Property Management"
                    ].map((type, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#22d3ee] rounded-full"></div>
                        <span className="text-sm">{type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-16 md:py-20 bg-[#0a1628]">
        <div className="container px-4">
          <div className="text-center mb-12">
            <p className="text-[#22d3ee] font-semibold mb-2 text-sm tracking-wider">PORTFOLIO</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">Our Work</h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Browse through our recent projects and see the quality craftsmanship we bring to every job.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-lg aspect-[4/3] cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <img
                  src={image.src}
                  alt={image.title}
                  width={image.width}
                  height={image.height}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3 md:p-4">
                  <div>
                    <p className="text-white font-semibold text-sm md:text-base">{image.title}</p>
                    <p className="text-white/80 text-xs md:text-sm">{image.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Serving Cities Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-[#1e3a8a]">Serving 13+ Cities Across Santa Barbara, Ventura & LA Counties</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">Danis Painting Co. is a licensed painting contractor proudly serving communities throughout Southern and Central California with professional painting and waterproofing services.</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 max-w-5xl mx-auto">
            {[
              { name: "Santa Barbara", slug: "santa-barbara" },
              { name: "Ventura", slug: "ventura" },
              { name: "Montecito", slug: "montecito" },
              { name: "Goleta", slug: "goleta" },
              { name: "Carpinteria", slug: "carpinteria" },
              { name: "Ojai", slug: "ojai" },
              { name: "Camarillo", slug: "camarillo" },
              { name: "Solvang", slug: "solvang" },
              { name: "Buellton", slug: "buellton" },
              { name: "Santa Ynez", slug: "santa-ynez" },
              { name: "Isla Vista", slug: "isla-vista" },
              { name: "Summerland", slug: "summerland" },
              { name: "Oxnard", slug: "oxnard" },
              { name: "Los Angeles", slug: "los-angeles" }
            ].map((location) => (
              <Link key={location.slug} href={`/city/${location.slug}`}>
                <Card className="group hover:shadow-lg transition-all duration-300 border-[#1e3a8a]/20 hover:border-[#0066cc] cursor-pointer h-full">
                  <CardContent className="p-6 flex flex-col items-center justify-center text-center h-full">
                    <MapPin className="w-8 h-8 text-[#0066cc] mb-3 group-hover:scale-110 transition-transform" />
                    <p className="font-semibold text-[#1e3a8a] group-hover:text-[#0066cc] transition-colors text-lg">{location.name}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Service × City Internal Links — SEO deep-link grid */}
      <section className="py-14 bg-white border-t border-gray-100">
        <div className="container px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-2 text-[#1e3a8a]">Painting Services by City</h2>
          <p className="text-center text-gray-500 mb-10 max-w-2xl mx-auto text-sm">Find expert painting contractors in your city. Click any link for a dedicated service page with local pricing and reviews.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {([
              { city: "Santa Barbara", slug: "santa-barbara", svcs: [
                ["Interior & Exterior Painting", "interior-exterior-painting"],
                ["Commercial Painting", "commercial-painting"],
                ["Cabinet Painting", "cabinet-painting"],
                ["Waterproofing", "waterproofing"],
              ]},
              { city: "Ventura", slug: "ventura", svcs: [
                ["Interior & Exterior Painting", "interior-exterior-painting"],
                ["Commercial Painting", "commercial-painting"],
                ["Roof Coating & Repair", "roof-coating-repair"],
                ["Pressure Washing", "pressure-washing"],
              ]},
              { city: "Goleta", slug: "goleta", svcs: [
                ["Interior & Exterior Painting", "interior-exterior-painting"],
                ["Commercial Painting", "commercial-painting"],
                ["Cabinet Painting", "cabinet-painting"],
                ["Drywall Repair", "drywall-repair"],
              ]},
              { city: "Montecito", slug: "montecito", svcs: [
                ["Interior & Exterior Painting", "interior-exterior-painting"],
                ["Cabinet Painting", "cabinet-painting"],
                ["Waterproofing", "waterproofing"],
                ["Deck & Staircase Repair", "deck-staircase-repair"],
              ]},
              { city: "Carpinteria", slug: "carpinteria", svcs: [
                ["Interior & Exterior Painting", "interior-exterior-painting"],
                ["Waterproofing", "waterproofing"],
                ["Roof Coating & Repair", "roof-coating-repair"],
                ["Pressure Washing", "pressure-washing"],
              ]},
              { city: "Ojai", slug: "ojai", svcs: [
                ["Interior & Exterior Painting", "interior-exterior-painting"],
                ["Cabinet Painting", "cabinet-painting"],
                ["Deck & Staircase Repair", "deck-staircase-repair"],
                ["Drywall Repair", "drywall-repair"],
              ]},
              { city: "Camarillo", slug: "camarillo", svcs: [
                ["Interior & Exterior Painting", "interior-exterior-painting"],
                ["Commercial Painting", "commercial-painting"],
                ["Waterproofing", "waterproofing"],
                ["Pressure Washing", "pressure-washing"],
              ]},
              { city: "Oxnard", slug: "oxnard", svcs: [
                ["Interior & Exterior Painting", "interior-exterior-painting"],
                ["Commercial Painting", "commercial-painting"],
                ["Waterproofing", "waterproofing"],
                ["Roof Coating & Repair", "roof-coating-repair"],
              ]},
              { city: "Los Angeles", slug: "los-angeles", svcs: [
                ["Interior & Exterior Painting", "interior-exterior-painting"],
                ["Commercial Painting", "commercial-painting"],
                ["Waterproofing", "waterproofing"],
                ["Roof Coating & Repair", "roof-coating"],
                ["Pressure Washing", "pressure-washing"],
              ]},
              { city: "Summerland", slug: "summerland", svcs: [
                ["Interior & Exterior Painting", "interior-exterior-painting"],
                ["Waterproofing", "waterproofing"],
                ["Deck & Staircase Repair", "deck-staircase-repair"],
                ["Pressure Washing", "pressure-washing"],
              ]},
            ] as { city: string; slug: string; svcs: [string, string][] }[]).map(({ city, slug, svcs }) => (
              <div key={slug} className="bg-gray-50 rounded-lg p-5 border border-gray-200 hover:border-[#1e3a8a]/40 transition-colors">
                <Link href={`/city/${slug}`} className="block font-bold text-[#1e3a8a] text-lg mb-3 hover:text-[#0066cc] transition-colors">
                  {city} Painters →
                </Link>
                <ul className="space-y-1.5">
                  {svcs.map(([name, svc]) => (
                    <li key={svc}>
                      <Link href={`/service/${svc}/${slug}`} className="text-sm text-gray-600 hover:text-[#0066cc] transition-colors flex items-center gap-1">
                        <span className="text-[#4caf30] text-xs">›</span> {name} in {city}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link href="/service-areas">
              <Button variant="outline" className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white bg-white">
                View All Service Areas
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Interactive Service Area Map */}
      <ServiceAreaMap />

      {/* Google Reviews Section - Real verified reviews from Google Business Profile */}
      <GoogleReviews maxReviews={5} variant="light" />



      {/* Latest Blog Posts Section */}
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Latest Painting Tips & Insights</h2>
              <p className="text-lg text-slate-600">Expert advice on painting, maintenance, and home improvement from Danis Painting Co.</p>
            </div>
            {/* Blog Posts Grid - 3 randomly selected posts */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {featuredBlogPosts.map((post) => (
                <Link key={post.slug} href={`/blog/${post.slug}`}>
                  <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer border-0 bg-white overflow-hidden">
                    {post.image && (
                      <div className="w-full h-48 overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          loading="lazy"
                          width="400"
                          height="192"
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="mb-3">
                        <span className="inline-block px-3 py-1 bg-[#4caf30]/10 text-[#3d9426] text-xs font-semibold rounded-full">
                          {post.category}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2 flex-grow">{post.title}</h3>
                      <p className="text-slate-600 text-sm mb-4 flex-grow">{post.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{post.readingTime} min read</span>
                        <span className="text-[#4caf30] font-semibold">Read More →</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            {/* View All Blog Posts Button */}
            <div className="text-center">
              <Link href="/blog">
                <Button size="lg" className="bg-[#4caf30] hover:bg-[#3d9426] text-white font-semibold">
                  View All Blog Posts
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-20 bg-[#1e3a8a]">
        <div className="container px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div className="space-y-6">
              <div>
              <p className="text-[#22d3ee] font-semibold mb-2 text-sm tracking-wider">GET IN TOUCH</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
                Get Your Free Painting Estimate Today
              </h2>
              <p className="text-white/70 mb-8">
                Contact us today for a free estimate. Whether it's a small touch-up or a complete renovation, we're here to bring your vision to life with honest New England quality.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-[#4caf30] hover:bg-[#3d9426] text-white"
                  onClick={() => {
                  trackPhoneCall();
                  window.location.href = "tel:805-403-8727";
                }}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  805-403-8727
                </Button>
                <Button
                  size="lg"
                  className="bg-white text-[#1e3a8a] hover:bg-white/90"
                  onClick={() => {
                    // Scroll to bottom to ensure chat widget is visible
                    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                    // Trigger chat widget open by dispatching a custom event
                    const event = new CustomEvent('openChat');
                    window.dispatchEvent(event);
                  }}
                >
                  Chat With Us
                </Button>
              </div>
              </div>
              <QuoteForm />
            </div>
            <div className="space-y-6">
              <div className="space-y-4">
                <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#4caf30] flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Call Mark Danis</h3>
                      <a
                        href="tel:805-403-8727"
                        onClick={trackPhoneCall}
                        className="text-white hover:text-[#22d3ee] transition-colors text-lg font-medium"
                      >
                        805-403-8727
                      </a>
                      <p className="text-white/60 text-sm mt-1">Available for calls and questions</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#4caf30] flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Email Us</h3>
                      <a
                        href="mailto:danispainting@gmail.com"
                        className="text-white hover:text-[#22d3ee] transition-colors"
                      >
                        danispainting@gmail.com
                      </a>
                      <p className="text-white/60 text-sm mt-1">We'll respond within 24 hours</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#4caf30] flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Service Area</h3>
                      <p className="text-white">Santa Barbara, Ventura & Los Angeles Counties</p>
                      <p className="text-white/60 text-sm mt-1">Proudly serving Southern & Central California</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>


      <Footer />

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors z-10"
            aria-label="Close lightbox"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Previous button */}
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors bg-black/30 hover:bg-black/50 rounded-full p-2"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          {/* Next button */}
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors bg-black/30 hover:bg-black/50 rounded-full p-2"
            aria-label="Next image"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          {/* Image container */}
          <div 
            className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={galleryImages[lightboxIndex].src}
              alt={galleryImages[lightboxIndex].title}
              className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
              loading="lazy"
            />
            <div className="text-center mt-4 px-4">
              <h3 className="text-white text-xl font-semibold">{galleryImages[lightboxIndex].title}</h3>
              <p className="text-white/70 text-sm mt-1">{galleryImages[lightboxIndex].description}</p>
              <p className="text-white/50 text-xs mt-2">{lightboxIndex + 1} / {galleryImages.length}</p>
            </div>
          </div>
        </div>
      )}

      {/* Chat Widget */}
    </div>
  );
}
